import { toast } from "react-toastify";
import axiosInstance from "../config/axios";

// Check if Commission Trust EFT already exists for a trade and type (optionally filtered by agentId)
export const checkCommissionTrustEFT = async (
  tradeId,
  type,
  agentId = null,
) => {
  try {
    let url = `/commission-trust-eft/check-existing/${tradeId}/${type}`;
    // Add agentId as query parameter if provided (for AgentCommissionTransfer type)
    if (agentId) {
      url += `?agentId=${agentId}`;
    }
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    toast.error("Error checking Commission Trust EFT");
    return { exists: false };
  }
};

// Check if Real Estate Trust EFT already exists for a trade and type
export const checkRealEstateTrustEFT = async (tradeId, type) => {
  try {
    const response = await axiosInstance.get(
      `/real-estate-trust-eft/check-existing/${tradeId}/${type}`,
    );
    return response.data;
  } catch (error) {
    toast.error("Error checking Real Estate Trust EFT");
    return { exists: false };
  }
};

/** EFT types shown in dedicated trade record sheet sections, not Commission Trust Activity. */
export const isExcludedFromCommissionTrustActivity = (eft) => {
  if (!eft) return true;
  const type = eft.type || "";
  if (
    type === "BuilderRepayment" ||
    type === "GeneralToCommissionTransfer"
  ) {
    return true;
  }
  const desc = String(eft.description || "").toLowerCase();
  if (desc.includes("builder repayment")) return true;
  if (
    desc.includes("commission transfer from general to commission") ||
    desc.includes("general to commission")
  ) {
    return true;
  }
  return false;
};

export const filterCommissionTrustActivityEFTs = (efts) =>
  (efts || []).filter((eft) => !isExcludedFromCommissionTrustActivity(eft));

/** Ledger / trial-balance reversal refs (REV#trade, REV-*, REV) — not real EFT numbers. */
export const isReversalLedgerEftRef = (value) => {
  const eft = String(value || "").trim();
  return /^REV$/i.test(eft) || /^REV-/i.test(eft) || /^REV#/i.test(eft);
};

/** Numeric EFT from ledger eftNumber or EFT# reference. */
export const extractDepositEftNumber = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const eftMatch = raw.match(/EFT#?\s*(\d+)/i);
  if (eftMatch) return eftMatch[1];
  if (/^\d+$/.test(raw)) return raw;
  return null;
};

/**
 * Show the real finalize/deposit EFT # on trade record sheets, not REV# reversal mirrors.
 */
export const resolveCommissionTrustDisplayEftNumber = (
  ledgerEntry,
  options = {},
) => {
  const {
    eftReversals = [],
    realEstateTrustEFTs = [],
    commissionTrustEFTs = [],
    ledgerEntries = [],
  } = options;

  const fromEntry =
    extractDepositEftNumber(ledgerEntry?.eftNumber) ||
    extractDepositEftNumber(ledgerEntry?.reference) ||
    String(ledgerEntry?.eftNumber || "").trim();

  if (fromEntry && !isReversalLedgerEftRef(fromEntry)) {
    return fromEntry;
  }

  const depositRev = (eftReversals || []).find(
    (r) =>
      r.section === "deposit-received" &&
      r.sourceEftNumber != null &&
      String(r.sourceEftNumber).trim() !== "",
  );
  if (depositRev?.sourceEftNumber) {
    return String(depositRev.sourceEftNumber);
  }

  const transfer = (realEstateTrustEFTs || []).find(
    (e) => e.type === "CommissionTransfer" && e.eftNumber != null,
  );
  if (transfer?.eftNumber != null) {
    return String(transfer.eftNumber);
  }

  for (const row of ledgerEntries || []) {
    if (row === ledgerEntry) continue;
    const num =
      extractDepositEftNumber(row?.eftNumber) ||
      extractDepositEftNumber(row?.reference);
    if (num && !isReversalLedgerEftRef(num)) return num;
  }

  for (const eft of commissionTrustEFTs || []) {
    const num = extractDepositEftNumber(eft?.eftNumber);
    if (num && !isReversalLedgerEftRef(num)) return num;
  }

  return fromEntry || "";
};

export const formatCommissionTrustActivityEftNumber = (eft, options = {}) => {
  const raw = eft?.eftNumber != null ? String(eft.eftNumber) : "";
  if (!raw) return "";
  if (!isReversalLedgerEftRef(raw)) {
    return extractDepositEftNumber(raw) || raw;
  }
  return resolveCommissionTrustDisplayEftNumber(
    { eftNumber: raw, reference: eft?.reference },
    options,
  );
};

// Get EFT type display name
export const getEFTTypeDisplayName = (type) => {
  const typeMap = {
    AgentCommissionTransfer: "Agent Commission Transfer",
    RefundOfDeposit: "Refund of Deposit",
    OutsideBrokerCommission: "Commission To Other Brokers",
    OurBrokerageCommission: "Our Brokerage Commission",
    BuilderRepayment: "Builder Repayment",
    GeneralToCommissionTransfer: "General to Commission Transfer",
    CommissionTransfer: "Commission Transfer",
    BalanceOfDeposit: "Balance of Deposit",
  };
  return typeMap[type] || type;
};

// Get payment plan display name
export const getPaymentPlanDisplayName = (plan) => {
  const planMap = {
    plan250: "Plan 250",
    plan500: "Plan 500",
    plan9010: "Plan 90/10",
    plan955: "Plan 95/5",
    plan8515: "Plan 85/15",
    flexible: "Flexible Plan",
    flatFee: "Flat Fee",
    garnishment: "Garnishment",
    buyerRebate: "Buyer Rebate",
    noFee: "No Fee",
    referralBroker: "Referral Broker",
  };
  return planMap[plan] || plan;
};

// Format date for display (M/D/YYYY — same as Real Estate Trust Payments on TRS)
export const formatDate = (dateString) => {
  if (!dateString) return "N/A";

  let date;
  if (dateString instanceof Date) {
    date = dateString;
  } else {
    const s = String(dateString).trim();
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      // Calendar day in local time — avoid UTC shifting YYYY-MM-DD
      date = new Date(
        parseInt(iso[1], 10),
        parseInt(iso[2], 10) - 1,
        parseInt(iso[3], 10),
      );
    } else {
      date = new Date(s);
    }
  }

  if (Number.isNaN(date.getTime())) return String(dateString);
  return date.toLocaleDateString("en-US");
};

// Check if General Account EFT invoice number already exists
export const checkGeneralAccountInvoice = async (invoiceNumber) => {
  try {
    const response = await axiosInstance.get(
      `/general-account-eft/check-invoice/${invoiceNumber}`,
    );
    return response.data;
  } catch (error) {
    toast.error("Error checking invoice number");
    return { exists: false };
  }
};

// Format a number as currency with dollar sign and comma (e.g., $23,000.00)
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === "") return "$0.00";
  const num =
    typeof amount === "string" ? parseFloat(amount.replace(/,/g, "")) : amount;
  if (isNaN(num)) return "$0.00";
  return num.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

/**
 * Dynamically extracts party names from trade based on deal type
 * @param {Object} trade - The trade object
 * @returns {Object} Object containing firstParty and secondParty names
 */
export const getPartyNamesByDealType = (trade) => {
  if (!trade || !trade.people || !Array.isArray(trade.people)) {
    return { firstParty: "N/A", secondParty: "N/A" };
  }

  const dealType = trade.keyInfo?.dealType || "";

  // Helper function to get the display name for a person
  const getPersonDisplayName = (person) => {
    // If company name exists and is not empty, use it
    if (person.companyName && person.companyName.trim() !== "") {
      return person.companyName.trim();
    }
    // Otherwise, use first name and last name
    const fullName = `${person.firstName || ""} ${
      person.lastName || ""
    }`.trim();
    return fullName || "N/A";
  };

  if (dealType === "Lease") {
    // For Lease deals: Landlord and Tenant
    const landlords = trade.people
      .filter((p) => p.type === "Landlord")
      .map(getPersonDisplayName)
      .join(", ");
    const tenants = trade.people
      .filter((p) => p.type === "Tenant")
      .map(getPersonDisplayName)
      .join(", ");

    return {
      firstParty: landlords || "N/A",
      secondParty: tenants || "N/A",
      firstPartyLabel: "Landlord",
      secondPartyLabel: "Tenant",
    };
  } else {
    // For Sale deals: Seller and Buyer (default)
    const sellers = trade.people
      .filter((p) => p.type === "Seller")
      .map(getPersonDisplayName)
      .join(", ");
    const buyers = trade.people
      .filter((p) => p.type === "Buyer")
      .map(getPersonDisplayName)
      .join(", ");

    return {
      firstParty: sellers || "N/A",
      secondParty: buyers || "N/A",
      firstPartyLabel: "Seller",
      secondPartyLabel: "Buyer",
    };
  }
};
